import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Check, Sparkles, Crown, Tag, Loader2 } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SubscriptionPlans = () => {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState('annual');
  const [couponCode, setCouponCode] = useState('');
  const [couponData, setCouponData] = useState(null);
  const [couponError, setCouponError] = useState('');
  const [isValidating, setIsValidating] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const response = await axios.get(`${API}/subscriptions/plans`);
      setPlans(response.data);
    } catch (error) {
      console.error('Error fetching plans:', error);
    }
  };

  const validateCoupon = async () => {
    if (!couponCode.trim()) return;
    
    setIsValidating(true);
    setCouponError('');
    setCouponData(null);
    
    try {
      const response = await axios.post(`${API}/subscriptions/validate-coupon`, {
        code: couponCode,
        plan_id: selectedPlan
      }, { withCredentials: true });
      
      setCouponData(response.data);
    } catch (error) {
      setCouponError(error.response?.data?.detail || 'Cupom inválido');
    } finally {
      setIsValidating(false);
    }
  };

  const isFree = couponData && couponData.final_price === 0;

  const handleFreeActivation = async () => {
    setIsLoading(true);
    try {
      await axios.post(`${API}/subscriptions/activate-with-coupon`, {
        coupon_code: couponData.code,
        plan_id: selectedPlan,
      }, { withCredentials: true });
      // Força recarga completa para que o AuthContext reconheça a assinatura ativada
      window.location.href = '/dashboard';
    } catch (error) {
      alert(error.response?.data?.detail || 'Erro ao ativar plano.');
      setIsLoading(false);
    }
  };

  const handleSubscribe = async () => {
    if (isFree) { handleFreeActivation(); return; }
    setIsLoading(true);

    try {
      const response = await axios.post(`${API}/subscriptions/create-checkout`, {
        plan_id: selectedPlan,
        origin_url: window.location.origin,
        coupon_code: couponData?.code || null
      }, { withCredentials: true });

      window.location.href = response.data.checkout_url;
    } catch (error) {
      console.error('Error creating checkout:', error);
      alert(error.response?.data?.detail || 'Erro ao processar pagamento');
      setIsLoading(false);
    }
  };

  const getSelectedPlan = () => plans.find(p => p.id === selectedPlan);
  
  const getFinalPrice = () => {
    const plan = getSelectedPlan();
    if (!plan) return 0;
    return couponData?.final_price ?? plan.price;
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50 to-white py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 rounded-full mb-4">
            <Crown className="w-8 h-8 text-purple-600" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
            Escolha seu Plano
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Acesse todo o conteúdo da plataforma Simby e desenvolva sua inteligência relacional
          </p>
        </div>

        {/* Plans */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          {/* Monthly Plan */}
          <Card 
            className={`p-6 cursor-pointer transition-all ${
              selectedPlan === 'monthly' 
                ? 'ring-2 ring-purple-500 border-purple-500' 
                : 'hover:border-purple-300'
            }`}
            onClick={() => {
              setSelectedPlan('monthly');
              setCouponData(null);
            }}
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-xl font-bold text-gray-900">Plano Mensal</h3>
                <p className="text-gray-600 text-sm">Flexibilidade total</p>
              </div>
              <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                selectedPlan === 'monthly' 
                  ? 'border-purple-500 bg-purple-500' 
                  : 'border-gray-300'
              }`}>
                {selectedPlan === 'monthly' && <Check className="w-4 h-4 text-white" />}
              </div>
            </div>
            
            <div className="mb-4">
              <span className="text-4xl font-bold text-gray-900">R$ 29,90</span>
              <span className="text-gray-600">/mês</span>
            </div>
            
            <ul className="space-y-2 text-sm text-gray-600">
              <li className="flex items-center"><Check className="w-4 h-4 text-green-500 mr-2" /> Acesso completo à plataforma</li>
              <li className="flex items-center"><Check className="w-4 h-4 text-green-500 mr-2" /> Todas as ilhas e lições</li>
              <li className="flex items-center"><Check className="w-4 h-4 text-green-500 mr-2" /> Simuladores de conversas</li>
              <li className="flex items-center"><Check className="w-4 h-4 text-green-500 mr-2" /> Diário de emoções</li>
            </ul>
          </Card>

          {/* Annual Plan */}
          <Card 
            className={`p-6 cursor-pointer transition-all relative ${
              selectedPlan === 'annual' 
                ? 'ring-2 ring-purple-500 border-purple-500' 
                : 'hover:border-purple-300'
            }`}
            onClick={() => {
              setSelectedPlan('annual');
              setCouponData(null);
            }}
          >
            <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
              <span className="bg-gradient-to-r from-purple-600 to-pink-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                ECONOMIZE 30%
              </span>
            </div>
            
            <div className="flex items-start justify-between mb-4 mt-2">
              <div>
                <h3 className="text-xl font-bold text-gray-900">Plano Anual</h3>
                <p className="text-gray-600 text-sm">Melhor custo-benefício</p>
              </div>
              <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                selectedPlan === 'annual' 
                  ? 'border-purple-500 bg-purple-500' 
                  : 'border-gray-300'
              }`}>
                {selectedPlan === 'annual' && <Check className="w-4 h-4 text-white" />}
              </div>
            </div>
            
            <div className="mb-4">
              <span className="text-4xl font-bold text-gray-900">R$ 249,00</span>
              <span className="text-gray-600">/ano</span>
              <p className="text-sm text-green-600 font-medium">Equivale a R$ 20,75/mês</p>
            </div>
            
            <ul className="space-y-2 text-sm text-gray-600">
              <li className="flex items-center"><Check className="w-4 h-4 text-green-500 mr-2" /> Tudo do plano mensal</li>
              <li className="flex items-center"><Check className="w-4 h-4 text-green-500 mr-2" /> Economia de R$ 109,80/ano</li>
              <li className="flex items-center"><Check className="w-4 h-4 text-green-500 mr-2" /> Acesso prioritário a novidades</li>
              <li className="flex items-center"><Sparkles className="w-4 h-4 text-purple-500 mr-2" /> Suporte prioritário</li>
            </ul>
          </Card>
        </div>

        {/* Coupon Section */}
        <Card className="p-6 mb-8">
          <div className="flex items-center space-x-2 mb-4">
            <Tag className="w-5 h-5 text-purple-600" />
            <h3 className="font-semibold text-gray-900">Tem um cupom de desconto?</h3>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-3">
            <input
              type="text"
              value={couponCode}
              onChange={(e) => {
                setCouponCode(e.target.value.toUpperCase());
                setCouponError('');
                setCouponData(null);
              }}
              placeholder="Digite o código do cupom"
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
            <Button 
              onClick={validateCoupon}
              disabled={isValidating || !couponCode.trim()}
              variant="outline"
              className="border-purple-500 text-purple-600 hover:bg-purple-50"
            >
              {isValidating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Aplicar'}
            </Button>
          </div>
          
          {couponError && (
            <p className="mt-2 text-sm text-red-600">{couponError}</p>
          )}
          
          {couponData && (
            <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-green-700 font-medium">
                ✓ Cupom aplicado! Desconto de {
                  couponData.discount_type === 'percentage'
                    ? `${couponData.discount_value}%`
                    : `R$ ${couponData.discount_amount.toFixed(2)}`
                }
              </p>
              {isFree && couponData.grant_duration_months ? (
                <p className="text-sm text-green-600 font-semibold">
                  🎁 Acesso completo grátis por {couponData.grant_duration_months} meses!
                </p>
              ) : (
                <p className="text-sm text-green-600">
                  De R$ {couponData.original_price.toFixed(2)} por R$ {couponData.final_price.toFixed(2)}
                </p>
              )}
            </div>
          )}
        </Card>

        {/* Summary & CTA */}
        <Card className="p-6 bg-gradient-to-r from-purple-600 to-purple-700 text-white">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div>
              <p className="text-purple-200 text-sm">Total a pagar</p>
              <div className="flex items-baseline gap-2">
                {couponData && (
                  <span className="text-xl line-through text-purple-300">
                    R$ {getSelectedPlan()?.price.toFixed(2)}
                  </span>
                )}
                <span className="text-3xl font-bold">
                  R$ {getFinalPrice().toFixed(2)}
                </span>
              </div>
              <p className="text-purple-200 text-sm">
                {selectedPlan === 'annual' ? 'Cobrança única anual' : 'Cobrança mensal'}
              </p>
            </div>
            
            <Button
              onClick={handleSubscribe}
              disabled={isLoading}
              size="lg"
              className="w-full sm:w-auto bg-white text-purple-700 hover:bg-purple-50 font-semibold px-8"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  {isFree ? 'Ativando...' : 'Processando...'}
                </>
              ) : isFree ? (
                '🎁 Ativar Plano Grátis'
              ) : (
                'Assinar agora'
              )}
            </Button>
          </div>
        </Card>

        {/* Security note */}
        <p className="text-center text-sm text-gray-500 mt-6">
          🔒 Pagamento seguro processado pelo Stripe. Cancele quando quiser.
        </p>
      </div>
    </div>
  );
};

export default SubscriptionPlans;

